import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, Plus, Printer, RotateCcw, Save, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WmsReceivingStickerPanel } from '@/components/wms/WmsReceivingStickerPanel';
import { api } from '@/lib/api';
import { usePageParams } from '@/hooks/usePageParams';

type InboundOrderLine = {
  id: string;
  line_number: number | null;
  sku_id: string | null;
  sku_code: string | null;
  sku_name: string | null;
  product_category: string | null;
  qty_expected: number | null;
  qty_received: number | null;
  uom: string | null;
  inspection_required: boolean | null;
  inspection_status: string | null;
  notes: string | null;
  status: string | null;
  source_order_line_id?: string | null;
  line_length?: string | null;
  heat_no?: string | null;
  mill_cert_no?: string | null;
  country_of_origin?: string | null;
  putaway_location_code?: string | null;
  putaway_location_id?: string | null;
  metadata?: Record<string, unknown> | null;
  inventory_code?: string | null;
  inventoryCode?: string | null;
  attachment_count?: number | null;
  attachment_files?: Array<{
    id: string;
    filename?: string | null;
    document_type?: string | null;
  }> | null;
};

type InboundOrderDetail = {
  id: string;
  asn_number: string | null;
  inbound_mode?: string | null;
  source_order_type?: string | null;
  project_code: string | null;
  source_order_number: string | null;
  source_order_id?: string | null;
  expected_date: string | null;
  warehouse_name: string | null;
  origin_name: string | null;
  status: string | null;
  created_by_username: string | null;
  warehouse_id?: string | null;
  notes: string | null;
  lines: InboundOrderLine[];
};

type PurchaseOrderLine = {
  id: string;
  line_number: number | null;
  sku_id?: string | null;
  sku_code?: string | null;
  sku_name?: string | null;
  item_type?: string | null;
  qty_ordered?: number | null;
  qty_fulfilled?: number | null;
  uom?: string | null;
};

type PurchaseOrderDetail = {
  id: string;
  order_number?: string | null;
  counterparty_name?: string | null;
  project_code?: string | null;
  lines?: PurchaseOrderLine[];
};

type BinOption = {
  id: string;
  code: string;
  name?: string | null;
  zone_name?: string | null;
};

type LineDraft = {
  qtyReceived: string;
  heatNo: string;
  millCertNo: string;
  countryOfOrigin: string;
  putawayLocationCode: string;
};

type WmsInboundOrderDetailPanelProps = {
  id?: string;
  embedded?: boolean;
  onClose?: () => void;
};

function formatStatusLabel(status: string | null | undefined) {
  if (!status) return 'Unknown';
  return status
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function statusTone(status: string | null | undefined) {
  switch (status) {
    case 'COMPLETED':
    case 'RECEIVED':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'QC_PENDING':
    case 'PUTAWAY_PENDING':
    case 'RECEIVING':
    case 'SUBMITTED':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'CANCELLED':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB').format(date);
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

const DIMENSION_TO_MM_FACTOR: Record<string, number> = {
  MM: 1,
  CM: 10,
  M: 1000,
  ML: 1000,
  INCH: 25.4,
  FT: 304.8,
};

function parseDimensionText(value: unknown): { value: number | null; uom: string | null } {
  if (value === undefined || value === null) return { value: null, uom: null };
  const text = String(value).trim();
  if (!text) return { value: null, uom: null };
  const match = text.match(/(\d+(?:[.,]\d+)?)\s*(MM|CM|M|ML|INCH|FT)\b/i);
  if (!match) return { value: null, uom: null };
  return {
    value: Number(String(match[1]).replace(',', '.')),
    uom: String(match[2]).toUpperCase(),
  };
}

function convertDimensionValueToMm(value: unknown, uom: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const normalizedUom = String(uom ?? 'MM').trim().toUpperCase() || 'MM';
  const factor = DIMENSION_TO_MM_FACTOR[normalizedUom] ?? 1;
  const converted = numeric * factor;
  return Number.isInteger(converted) ? converted : Number(converted.toFixed(3));
}

function formatMmLength(value: unknown, uom: unknown): string {
  const mm = convertDimensionValueToMm(value, uom);
  if (mm == null) return '';
  return `${Number.isInteger(mm) ? String(mm) : String(mm)} MM`;
}

function inventoryCodeOf(line: InboundOrderLine) {
  const metadata = asRecord(line.metadata);
  const value = line.inventory_code
    ?? line.inventoryCode
    ?? metadata.inventory_code
    ?? metadata.inventoryCode
    ?? metadata.sourceInventoryCode
    ?? (Array.isArray(metadata.sourceInventoryCodes) ? metadata.sourceInventoryCodes[0] : null)
    ?? metadata.originInventoryCode
    ?? (Array.isArray(metadata.originInventoryCodes) ? metadata.originInventoryCodes[0] : null);
  if (typeof value === 'string' && value.trim()) return value.trim();
  return '-';
}

function parseLengthFromDescription(value: string | null | undefined) {
  const parsed = parseDimensionText(value);
  return formatMmLength(parsed.value, parsed.uom);
}

function lineLengthOf(line: InboundOrderLine) {
  const metadata = asRecord(line.metadata);
  const explicitLength = formatMmLength(
    metadata.length_mm ?? metadata.length_value,
    metadata.dimension_uom ?? (metadata.length_mm != null ? 'MM' : null),
  );
  if (explicitLength) return explicitLength;
  if (typeof line.line_length === 'string' && line.line_length.trim()) {
    const parsedLineLength = parseDimensionText(line.line_length);
    return formatMmLength(parsedLineLength.value, parsedLineLength.uom) || line.line_length.trim();
  }
  const candidates = [
    metadata.line_length,
    metadata.pipe_length,
    metadata.spec_length,
    metadata.length,
    metadata.length_mm != null ? `${String(metadata.length_mm).trim()} MM` : null,
    parseLengthFromDescription(line.sku_name),
  ];
  for (const candidate of candidates) {
    const parsed = parseDimensionText(candidate);
    const normalized = formatMmLength(parsed.value, parsed.uom);
    if (normalized) return normalized;
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  return '';
}

function draftQtyOf(line: InboundOrderLine, headerStatus?: string | null) {
  if (String(headerStatus ?? '').toUpperCase() === 'DRAFT') {
    const metadata = asRecord(line.metadata);
    const draftQty = Number(metadata.draft_qty_received ?? 0);
    if (Number.isFinite(draftQty) && draftQty >= 0) return draftQty;
  }
  return line.qty_received ?? 0;
}

function createDraft(line: InboundOrderLine, headerStatus?: string | null): LineDraft {
  return {
    qtyReceived: String(draftQtyOf(line, headerStatus)),
    heatNo: String(line.heat_no ?? ''),
    millCertNo: String(line.mill_cert_no ?? ''),
    countryOfOrigin: String(line.country_of_origin ?? asRecord(line.metadata).country_of_origin ?? ''),
    putawayLocationCode: String(line.putaway_location_code ?? ''),
  };
}

function returnReceivedQtyOf(line: InboundOrderLine) {
  const expected = Number(line.qty_expected ?? 1);
  if (Number.isFinite(expected) && expected > 0) return Math.min(1, expected);
  return 1;
}

function qcReadyQty(line: InboundOrderLine) {
  if (!line.inspection_required) return 0;
  const qtyReceived = Number(line.qty_received ?? 0);
  if (qtyReceived <= 0) return 0;
  const inspectionStatus = String(line.inspection_status ?? '').toUpperCase();
  if (inspectionStatus === 'FAILED') return 0;
  if (inspectionStatus === 'PENDING' || inspectionStatus === 'PASSED') return qtyReceived;
  return 0;
}

function qcStatusLabel(line: InboundOrderLine) {
  const inspectionRequired = Boolean(line.inspection_required);
  const qtyReceived = Number(line.qty_received ?? 0);
  const inspectionStatus = String(line.inspection_status ?? '').toUpperCase();

  if (!inspectionRequired) return 'Not Required';
  if (qtyReceived <= 0) return 'Set Receive Qty';
  if (inspectionStatus === 'PENDING') return 'QC Ready';
  if (inspectionStatus === 'PASSED') return 'QC Done';
  if (inspectionStatus === 'PARTIAL') return 'QC Partial';
  if (inspectionStatus === 'FAILED') return 'QC Failed';
  return formatStatusLabel(inspectionStatus || 'PENDING');
}

function qcStatusTone(line: InboundOrderLine) {
  const label = qcStatusLabel(line);
  switch (label) {
    case 'QC Done':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'QC Ready':
    case 'QC Partial':
    case 'Set Receive Qty':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'QC Failed':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

function currentStepLabel(line: InboundOrderLine) {
  const lineStatus = String(line.status ?? '').toUpperCase();
  const inspectionStatus = String(line.inspection_status ?? '').toUpperCase();
  const qtyReceived = Number(line.qty_received ?? 0);
  const inspectionRequired = Boolean(line.inspection_required);

  if (qtyReceived <= 0) return 'Pending Receiving';
  if (inspectionRequired) {
    if (inspectionStatus === 'PENDING' || lineStatus === 'QC_PENDING') return 'Pending QC';
    if (inspectionStatus === 'PARTIAL') return 'QC Partial';
    if (inspectionStatus === 'FAILED') return 'QC Failed';
    if (lineStatus === 'PUTAWAY_PENDING') return 'Pending Putaway';
    if (inspectionStatus === 'PASSED' && ['RECEIVED', 'COMPLETED'].includes(lineStatus)) return 'On-Hand';
  }
  if (lineStatus === 'PUTAWAY_PENDING') return 'Pending Putaway';
  if (['RECEIVED', 'COMPLETED'].includes(lineStatus)) return 'On-Hand';
  if (lineStatus === 'PARTIAL') return 'Partially Received';
  return formatStatusLabel(lineStatus || 'PENDING');
}

function currentStepTone(line: InboundOrderLine) {
  const label = currentStepLabel(line);
  switch (label) {
    case 'On-Hand':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'Pending QC':
    case 'Pending Putaway':
    case 'Partially Received':
    case 'QC Partial':
    case 'Pending Receiving':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'QC Failed':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

function isInboundLineLockedAfterQc(line: InboundOrderLine) {
  const lineStatus = String(line.status ?? '').toUpperCase();
  const inspectionStatus = String(line.inspection_status ?? '').toUpperCase();
  return ['RECEIVED', 'QC_PENDING', 'PUTAWAY_PENDING', 'COMPLETED'].includes(lineStatus)
    || inspectionStatus === 'PASSED';
}

function HeaderField({
  label,
  value,
  className = '',
}: {
  label: string;
  value: string | null | undefined;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 min-h-8 text-sm font-medium text-foreground">{value && value.trim() ? value : '-'}</div>
    </div>
  );
}

export function WmsInboundOrderDetailPanel({
  id: idProp,
  embedded = false,
  onClose,
}: WmsInboundOrderDetailPanelProps) {
  const navigate = useNavigate();
  const params = usePageParams<{ id: string }>();
  const id = idProp ?? params.id;

  const [detail, setDetail] = useState<InboundOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [savingLineId, setSavingLineId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [revising, setRevising] = useState(false);
  const [addingPoLines, setAddingPoLines] = useState(false);
  const [showStickerPanel, setShowStickerPanel] = useState(false);
  const [stickerPreviewHtml, setStickerPreviewHtml] = useState('');
  const [stickerPreviewTitle, setStickerPreviewTitle] = useState('Sticker Print Preview');
  const [showStickerPreview, setShowStickerPreview] = useState(false);
  const stickerPreviewFrameRef = useRef<HTMLIFrameElement | null>(null);
  const [uploadingLineId, setUploadingLineId] = useState<string | null>(null);
  const [deletingLineId, setDeletingLineId] = useState<string | null>(null);
  const [inlineWarning, setInlineWarning] = useState('');
  const [bins, setBins] = useState<BinOption[]>([]);
  const [poSelectorOpen, setPoSelectorOpen] = useState(false);
  const [poDetail, setPoDetail] = useState<PurchaseOrderDetail | null>(null);
  const [poSelectorLoading, setPoSelectorLoading] = useState(false);
  const [selectedPoLineIds, setSelectedPoLineIds] = useState<string[]>([]);
  const [poLineQtyDrafts, setPoLineQtyDrafts] = useState<Record<string, string>>({});

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get<{ data?: InboundOrderDetail } | InboundOrderDetail>(`/wms/inbound-orders/${id}`);
      const payload = (response as { data?: InboundOrderDetail })?.data ?? response as InboundOrderDetail;
      setDetail(payload);
      setDrafts(
        Object.fromEntries((payload.lines ?? []).map((line) => [line.id, createDraft(line, payload.status)])),
      );
      setInlineWarning('');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load inbound order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    const warehouseId = detail?.warehouse_id;
    if (!warehouseId) {
      setBins([]);
      return;
    }
    let cancelled = false;
    void api.get<BinOption[]>(`/wms/bins?warehouseId=${encodeURIComponent(warehouseId)}`)
      .then((rows) => {
        if (!cancelled) setBins(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setBins([]);
      });
    return () => {
      cancelled = true;
    };
  }, [detail?.warehouse_id]);

  const detailLines = detail?.lines ?? [];
  const draftedQtyBySourceLineId = useMemo(() => {
    const map = new Map<string, number>();
    for (const line of detailLines) {
      const sourceLineId = String(line.source_order_line_id ?? '').trim();
      if (!sourceLineId) continue;
      map.set(sourceLineId, (map.get(sourceLineId) ?? 0) + Number(line.qty_expected ?? 0));
    }
    return map;
  }, [detailLines]);
  const poSelectableLines = useMemo(() => (poDetail?.lines ?? []).map((line) => {
    const orderedQty = Number(line.qty_ordered ?? 0);
    const fulfilledQty = Number(line.qty_fulfilled ?? 0);
    const draftedQty = draftedQtyBySourceLineId.get(String(line.id)) ?? 0;
    const remainingQty = Math.max(0, orderedQty - fulfilledQty - draftedQty);
    return { ...line, orderedQty, fulfilledQty, draftedQty, remainingQty };
  }).filter((line) => line.remainingQty > 0), [draftedQtyBySourceLineId, poDetail?.lines]);
  const isReturnGrn = String(detail?.inbound_mode ?? '').toUpperCase() === 'RETURN'
    && String(detail?.source_order_type ?? '').toUpperCase() === 'MATERIAL_RETURN';
  const headerStatus = String(detail?.status ?? '').toUpperCase();
  const isDraftStatus = headerStatus === 'DRAFT';
  const isReceivingStatus = headerStatus === 'RECEIVING';
  const canEditReceiving = isDraftStatus || isReceivingStatus;
  const canEditTraceFields = ['DRAFT', 'RECEIVING', 'QC_PENDING', 'PUTAWAY_PENDING'].includes(headerStatus);
  const binCodeById = useMemo(() => new Map(bins.map((bin) => [String(bin.id), String(bin.code ?? '')])), [bins]);
  const canRevise = useMemo(
    () => {
      const hasQcOutcome = detailLines.some((line) => ['PASSED', 'FAILED', 'PARTIAL'].includes(String(line.inspection_status ?? '').toUpperCase()) || String(line.status ?? '').toUpperCase() === 'REJECTED');
      if (['CONFIRMED', 'QC_PENDING'].includes(headerStatus) && !hasQcOutcome) return true;
      return detailLines.some((line) => ['FAILED', 'PARTIAL'].includes(String(line.inspection_status ?? '').toUpperCase()) || String(line.status ?? '').toUpperCase() === 'REJECTED');
    },
    [detailLines, headerStatus],
  );
  const dirtyLineIds = useMemo(() => {
    const dirty = new Set<string>();
    for (const line of detailLines) {
      const draft = drafts[line.id];
      if (!draft) continue;
      const initial = createDraft(line, detail?.status);
      if (
        draft.qtyReceived !== initial.qtyReceived
        || draft.heatNo !== initial.heatNo
        || draft.millCertNo !== initial.millCertNo
        || draft.countryOfOrigin !== initial.countryOfOrigin
        || draft.putawayLocationCode !== initial.putawayLocationCode
      ) {
        dirty.add(line.id);
      }
    }
    return dirty;
  }, [detail?.status, detailLines, drafts]);

  const updateDraft = (lineId: string, patch: Partial<LineDraft>) => {
    setInlineWarning('');
    setDrafts((prev) => ({
      ...prev,
      [lineId]: {
        ...(prev[lineId] ?? { qtyReceived: '0', heatNo: '', millCertNo: '', countryOfOrigin: '', putawayLocationCode: '' }),
        ...patch,
      },
    }));
  };

  const resetAllLines = () => {
    setDrafts(
      Object.fromEntries(detailLines.map((line) => [line.id, createDraft(line, detail?.status)])),
    );
  };

  const saveLine = async (line: InboundOrderLine) => {
    const draft = drafts[line.id] ?? createDraft(line, detail?.status);
    const qtyReceived = isReturnGrn ? returnReceivedQtyOf(line) : (draft.qtyReceived.trim() === '' ? 0 : Number(draft.qtyReceived));
    const canSaveReceivingQty = canEditReceiving && !isInboundLineLockedAfterQc(line);
    if (canSaveReceivingQty && (!Number.isFinite(qtyReceived) || qtyReceived < 0)) {
      toast.error('Received qty must be a valid non-negative number');
      return;
    }
    const payload: Record<string, unknown> = {};
    if (canSaveReceivingQty) payload.qtyReceived = qtyReceived;
    if (!isReturnGrn && canEditTraceFields) {
      payload.heatNo = draft.heatNo.trim() || undefined;
      payload.millCertNo = draft.millCertNo.trim() || undefined;
      payload.countryOfOrigin = draft.countryOfOrigin.trim() || undefined;
    }
    const putawayLocationCode = draft.putawayLocationCode.trim()
      || (line.putaway_location_id ? binCodeById.get(String(line.putaway_location_id)) : '')
      || '';
    if (putawayLocationCode && (canSaveReceivingQty || canEditTraceFields || isReturnGrn)) {
      payload.putawayLocationCode = putawayLocationCode;
    }
    if (Object.keys(payload).length === 0) return;
    setSavingLineId(line.id);
    try {
      await api.patch(`/wms/inbound-orders/${detail?.id}/lines/${line.id}`, payload);
      toast.success(`Updated line ${line.line_number ?? ''}`.trim());
      await fetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update inbound line');
    } finally {
      setSavingLineId(null);
    }
  };

  const receiveLine = async (line: InboundOrderLine) => {
    const draft = drafts[line.id] ?? createDraft(line, detail?.status);
    const qtyReceived = isReturnGrn ? returnReceivedQtyOf(line) : (draft.qtyReceived.trim() === '' ? 0 : Number(draft.qtyReceived));
    if (!Number.isFinite(qtyReceived) || qtyReceived <= 0) {
      toast.error(`Line ${line.line_number ?? ''}: Received qty must be greater than 0`.trim());
      return;
    }
    const putawayLocationCode = draft.putawayLocationCode.trim()
      || (line.putaway_location_id ? binCodeById.get(String(line.putaway_location_id)) : '')
      || '';
    if (isReturnGrn && !putawayLocationCode) {
      setInlineWarning(`Line ${line.line_number ?? ''} must indicate Putaway Location before Received.`.trim());
      return;
    }
    await saveLine(line);
    setConfirming(true);
    try {
      await api.post(`/wms/inbound-orders/${detail?.id}/receive`, {
        lines: [{
          lineId: line.id,
          qtyReceived,
          qtyDamaged: 0,
          qtyRejected: 0,
          putawayLocationCode: putawayLocationCode || undefined,
          notes: line.notes ?? undefined,
        }],
      });
      toast.success(`Received line ${line.line_number ?? ''}`.trim());
      await fetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to receive line');
    } finally {
      setConfirming(false);
    }
  };

  const uploadMillCert = async (line: InboundOrderLine, files: FileList | null) => {
    const file = files?.[0];
    if (!file || !detail?.id) return;
    setUploadingLineId(line.id);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('referenceType', 'ASN_LINE');
      formData.append('referenceId', line.id);
      formData.append('documentType', 'MILL_CERT');
      await api.post('/wms/attachments', formData);
      toast.success(`Uploaded mill cert for line ${line.line_number ?? ''}`.trim());
      await fetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to upload mill cert file');
    } finally {
      setUploadingLineId(null);
    }
  };

  const saveAllLines = async () => {
    const changedLines = detailLines.filter((line) => dirtyLineIds.has(line.id));
    if (changedLines.length === 0) return;
    for (const line of changedLines) {
      // keep sequential writes so server-side totals and statuses stay predictable
      // for each line update.
      // eslint-disable-next-line no-await-in-loop
      await saveLine(line);
    }
  };

  const deleteLine = async (line: InboundOrderLine) => {
    if (!detail?.id || deletingLineId || confirming || savingLineId) return;
    if (currentStepLabel(line) !== 'Pending Receiving') {
      toast.error('Only pending receiving lines can be deleted');
      return;
    }
    if (!window.confirm(`Delete line ${line.line_number ?? ''} from ${detail.asn_number ?? 'this GRN'}?`.trim())) {
      return;
    }
    setDeletingLineId(line.id);
    try {
      await api.delete(`/wms/inbound-orders/${detail.id}/lines/${line.id}`);
      toast.success(`Deleted line ${line.line_number ?? ''}`.trim());
      await fetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to delete inbound line');
    } finally {
      setDeletingLineId(null);
    }
  };

  const cancelInboundOrder = async () => {
    if (!detail?.id || cancelling) return;
    if (!window.confirm(`Cancel inbound / GRN ${detail.asn_number ?? ''}? This will soft delete the wrong received order and attempt to rollback pending receiving effects.`.trim())) {
      return;
    }
    setCancelling(true);
    try {
      await api.delete(`/wms/inbound-orders/${detail.id}`);
      toast.success(`Cancelled ${detail.asn_number ?? 'inbound order'}`);
      if (onClose) {
        onClose();
      } else {
        navigate(-1);
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to cancel inbound order');
    } finally {
      setCancelling(false);
    }
  };

  const openPoLineSelector = async () => {
    if (!detail?.source_order_id || !isDraftStatus) return;
    setPoSelectorOpen(true);
    setPoSelectorLoading(true);
    try {
      const po = await api.get<PurchaseOrderDetail>(`/wms/business-orders/${detail.source_order_id}`);
      setPoDetail(po);
      const nextQtyDrafts: Record<string, string> = {};
      const nextSelected: string[] = [];
      for (const line of po.lines ?? []) {
        const remainingQty = Math.max(
          0,
          Number(line.qty_ordered ?? 0)
            - Number(line.qty_fulfilled ?? 0)
            - (draftedQtyBySourceLineId.get(String(line.id)) ?? 0),
        );
        if (remainingQty <= 0) continue;
        nextQtyDrafts[String(line.id)] = String(remainingQty);
      }
      setPoLineQtyDrafts(nextQtyDrafts);
      setSelectedPoLineIds(nextSelected);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to load PO lines');
      setPoDetail(null);
    } finally {
      setPoSelectorLoading(false);
    }
  };

  const addSelectedPoLines = async () => {
    if (!detail?.id || !isDraftStatus) return;
    const selected = poSelectableLines
      .filter((line) => selectedPoLineIds.includes(String(line.id)))
      .map((line) => ({
        sourceOrderLineId: String(line.id),
        qtyExpected: Number(poLineQtyDrafts[String(line.id)] ?? 0),
      }));
    if (selected.length === 0) {
      toast.error('Select at least one PO line');
      return;
    }
    for (const line of selected) {
      const sourceLine = poSelectableLines.find((item) => String(item.id) === line.sourceOrderLineId);
      if (!sourceLine || !Number.isFinite(line.qtyExpected) || line.qtyExpected <= 0 || line.qtyExpected > sourceLine.remainingQty) {
        toast.error(`Line ${sourceLine?.line_number ?? ''} has invalid add qty`);
        return;
      }
    }
    setAddingPoLines(true);
    try {
      const result = await api.post<{ added?: number }>(`/wms/inbound-orders/${detail.id}/add-open-po-lines`, { lines: selected });
      toast.success(`Added ${Number(result?.added ?? 0)} PO line(s)`);
      setPoSelectorOpen(false);
      setSelectedPoLineIds([]);
      setPoLineQtyDrafts({});
      await fetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to add remaining PO lines');
    } finally {
      setAddingPoLines(false);
    }
  };

  const receiveAllInboundOrder = async () => {
    if (!detail?.id || (!isDraftStatus && !isReceivingStatus)) return;
    if (dirtyLineIds.size > 0) {
      await saveAllLines();
    }
    const latestLines = detailLines.map((line) => {
      const draft = drafts[line.id] ?? createDraft(line, detail?.status);
      return {
        lineId: line.id,
        qtyReceived: isReturnGrn ? returnReceivedQtyOf(line) : (draft.qtyReceived.trim() === '' ? 0 : Number(draft.qtyReceived)),
        qtyDamaged: 0,
        qtyRejected: 0,
        putawayLocationCode: draft.putawayLocationCode.trim()
          || (line.putaway_location_id ? binCodeById.get(String(line.putaway_location_id)) : '')
          || undefined,
        notes: line.notes ?? undefined,
      };
    });
    for (const line of latestLines) {
      if (!Number.isFinite(Number(line.qtyReceived)) || Number(line.qtyReceived) <= 0) {
        toast.error('Every line must have received qty before Receive All');
        return;
      }
    }
    if (isReturnGrn) {
      const missingPutaway = detailLines.find((line) => {
        const draft = drafts[line.id] ?? createDraft(line, detail?.status);
        return Number(isReturnGrn ? returnReceivedQtyOf(line) : (draft.qtyReceived || 0)) > 0
          && !(draft.putawayLocationCode.trim() || (line.putaway_location_id ? binCodeById.get(String(line.putaway_location_id)) : ''));
      });
      if (missingPutaway) {
        setInlineWarning(`Line ${missingPutaway.line_number ?? ''} must indicate Putaway Location before Received.`.trim());
        return;
      }
    }
    setConfirming(true);
    try {
      await api.post(`/wms/inbound-orders/${detail.id}/receive`, {
        lines: latestLines,
      });
      toast.success(`Received all lines for ${detail.asn_number ?? 'GRN'}`);
      await fetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to confirm GRN');
    } finally {
      setConfirming(false);
    }
  };

  const reviseInboundOrder = async () => {
    if (!detail?.id || !canRevise) return;
    setRevising(true);
    try {
      await api.post(`/wms/inbound-orders/${detail.id}/revise`, {});
      toast.success(`Revised ${detail.asn_number ?? 'GRN'} back to draft`);
      await fetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to revise GRN');
    } finally {
      setRevising(false);
    }
  };

  const header = (
      <div className="shrink-0 border-b bg-background px-6 py-5">
      <div>
        <div className="pr-10">
          <div className="text-lg font-semibold tracking-tight">Inbound / GRN : {detail?.asn_number ?? '-'}</div>
          <div className="mt-1 text-sm text-muted-foreground">Inbound receiving detail</div>
          <div className="mt-2">
            <Badge className={`border ${statusTone(detail?.status)}`}>{formatStatusLabel(detail?.status)}</Badge>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-start gap-2">
          {(canEditReceiving || canEditTraceFields) && dirtyLineIds.size > 0 ? (
            <>
              <Button variant="outline" size="sm" onClick={resetAllLines} disabled={Boolean(savingLineId)}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Reset
              </Button>
              <Button size="sm" onClick={() => void saveAllLines()} disabled={Boolean(savingLineId)}>
                {savingLineId ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                Save Changes ({dirtyLineIds.size})
              </Button>
            </>
          ) : null}
          {isDraftStatus && detail?.source_order_id ? (
            <Button variant="outline" size="sm" onClick={() => void openPoLineSelector()} disabled={addingPoLines || confirming || revising}>
              {addingPoLines ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1.5 h-3.5 w-3.5" />}
              Add from PO
            </Button>
          ) : null}
          {canEditReceiving ? (
            <Button size="sm" onClick={() => void receiveAllInboundOrder()} disabled={confirming || Boolean(savingLineId) || addingPoLines}>
              {confirming ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />}
              {isReturnGrn ? 'Receive All' : 'Receive All'}
            </Button>
          ) : null}
          {!isDraftStatus && canRevise ? (
            <Button variant="outline" size="sm" onClick={() => void reviseInboundOrder()} disabled={revising || confirming}>
              {revising ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}
              Revise
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowStickerPanel(true)}
          >
            <Printer className="mr-1.5 h-3.5 w-3.5" />
            Print Stickers
          </Button>
          <Button variant="outline" size="sm" onClick={() => void cancelInboundOrder()} disabled={cancelling}>
            {cancelling ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Trash2 className="mr-1.5 h-3.5 w-3.5" />}
            Cancel
          </Button>
          {!embedded ? (
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              Back
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-6">
        <HeaderField label="GRN No" value={detail?.asn_number} />
        <HeaderField label="Project Code" value={detail?.project_code} />
        <HeaderField label="PO Number" value={detail?.source_order_number} />
        <HeaderField label="Expect Date" value={formatDate(detail?.expected_date)} />
        <HeaderField label="Warehouse Name" value={detail?.warehouse_name} />
        <HeaderField label="Supplier" value={detail?.origin_name} />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-6">
        <HeaderField label="Status" value={formatStatusLabel(detail?.status)} />
        <HeaderField label="Create By" value={detail?.created_by_username} />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-6">
        <HeaderField label="Remark" value={detail?.notes} className="lg:col-span-6" />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading inbound order...</span>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <div className="flex flex-1 items-center justify-center px-6">
          <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error || 'Inbound order not found'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      {header}

      <div className="min-h-0 flex-1 overflow-hidden px-6 py-4">
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          {inlineWarning ? (
            <div className="mx-auto mb-3 w-full max-w-4xl shrink-0">
              <div className="flex items-start justify-between gap-3 rounded-md border border-sky-300 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-900 shadow-sm">
                <div>{inlineWarning}</div>
                <button
                  type="button"
                  className="shrink-0 rounded p-1 text-sky-700 hover:bg-sky-100"
                  onClick={() => setInlineWarning('')}
                  aria-label="Close warning"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : null}
          <div className="mb-3 flex shrink-0 items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold">Lines</div>
              <div className="text-xs text-muted-foreground">{detailLines.length} row(s)</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => void fetchDetail()}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Refresh
            </Button>
          </div>

          <div className="runtime-grid-scrollbar boox-x-scroll min-h-0 flex-1 overflow-x-auto overflow-y-auto rounded-md border">
          <table className={`${isReturnGrn ? 'min-w-[1180px]' : 'min-w-[1940px]'} w-max border-collapse text-sm`}>
            <thead className="sticky top-0 z-10 bg-background">
              <tr className="border-b bg-slate-50">
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Line</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Inventory Code</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium">SKU Code</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Item Description</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Type</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Current Step</th>
                {!isReturnGrn ? <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Expected Qty</th> : null}
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Received Qty</th>
                {!isReturnGrn ? <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Length</th> : null}
                {!isReturnGrn ? <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Heat No</th> : null}
                {!isReturnGrn ? <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Mill Cert No</th> : null}
                {!isReturnGrn ? <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Country Of Origin</th> : null}
                {!isReturnGrn ? <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Document Upload</th> : null}
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Putaway Location</th>
                {!isReturnGrn ? <th className="whitespace-nowrap px-3 py-2 text-left font-medium">UOM</th> : null}
                {!isReturnGrn ? <th className="whitespace-nowrap px-3 py-2 text-left font-medium">QC Required</th> : null}
                {!isReturnGrn ? <th className="whitespace-nowrap px-3 py-2 text-left font-medium">Passed Qty</th> : null}
                <th className="sticky right-0 z-20 whitespace-nowrap border-l bg-slate-50 px-3 py-2 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {detailLines.map((line) => {
                const draft = drafts[line.id] ?? createDraft(line, detail?.status);
                const lineLocked = !canEditReceiving || isInboundLineLockedAfterQc(line);
                const linePendingReceiving = currentStepLabel(line) === 'Pending Receiving';
                const traceEditable = !isReturnGrn
                  && canEditTraceFields
                  && currentStepLabel(line) !== 'On-Hand';
                return (
                  <tr key={line.id} className="border-b align-top">
                    <td className="px-3 py-2 text-muted-foreground">{line.line_number ?? '-'}</td>
                    <td className="px-3 py-2 font-medium">{inventoryCodeOf(line)}</td>
                    <td className="px-3 py-2 font-medium">{line.sku_code ?? '-'}</td>
                    <td className="min-w-[300px] px-3 py-2">{line.sku_name ?? '-'}</td>
                    <td className="px-3 py-2">{line.product_category ?? '-'}</td>
                    <td className="px-3 py-2">
                      <Badge className={`border ${currentStepTone(line)}`}>{currentStepLabel(line)}</Badge>
                    </td>
                    {!isReturnGrn ? <td className="px-3 py-2">{line.qty_expected ?? 0}</td> : null}
                    <td className="px-3 py-2">
                      <Input
                        className="h-8 w-28"
                        type="number"
                        min="0"
                        step="0.01"
                        value={isReturnGrn ? String(returnReceivedQtyOf(line)) : draft.qtyReceived}
                        disabled={isReturnGrn || lineLocked}
                        onChange={(event) => updateDraft(line.id, { qtyReceived: event.target.value })}
                      />
                    </td>
                    {!isReturnGrn ? <td className="px-3 py-2">
                      <div className="min-h-8 min-w-28 rounded border border-transparent bg-muted/30 px-3 py-1.5 text-sm text-foreground">
                        {lineLengthOf(line) || '-'}
                      </div>
                    </td> : null}
                    {!isReturnGrn ? <td className="px-3 py-2">
                      <Input
                        className="h-8 min-w-[140px]"
                        value={draft.heatNo}
                        disabled={!traceEditable}
                        onChange={(event) => updateDraft(line.id, { heatNo: event.target.value })}
                        placeholder="Heat No"
                      />
                    </td> : null}
                    {!isReturnGrn ? <td className="px-3 py-2">
                      <Input
                        className="h-8 min-w-[160px]"
                        value={draft.millCertNo}
                        disabled={!traceEditable}
                        onChange={(event) => updateDraft(line.id, { millCertNo: event.target.value })}
                        placeholder="Mill Cert No"
                      />
                    </td> : null}
                    {!isReturnGrn ? <td className="px-3 py-2">
                      <Input
                        className="h-8 min-w-[160px]"
                        value={draft.countryOfOrigin}
                        disabled={!traceEditable}
                        onChange={(event) => updateDraft(line.id, { countryOfOrigin: event.target.value })}
                        placeholder="Country"
                      />
                    </td> : null}
                    {!isReturnGrn ? <td className="px-3 py-2 min-w-[220px]">
                      <div className="flex items-center gap-2">
                        <label className={`inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs font-medium ${traceEditable ? 'cursor-pointer bg-background hover:bg-muted' : 'cursor-not-allowed bg-muted/30 text-muted-foreground'}`}>
                          <Upload className="h-3.5 w-3.5" />
                          Upload
                          <input
                            type="file"
                            accept="application/pdf,image/jpeg,image/png,image/webp"
                            className="hidden"
                            disabled={!traceEditable || uploadingLineId === line.id}
                            onChange={(event) => {
                              void uploadMillCert(line, event.currentTarget.files);
                              event.currentTarget.value = '';
                            }}
                          />
                        </label>
                        <div className="min-w-0 text-xs text-muted-foreground">
                          {uploadingLineId === line.id
                            ? 'Uploading...'
                            : Number(line.attachment_count ?? 0) > 0
                              ? `${Number(line.attachment_count ?? 0)} file(s)`
                              : 'No file'}
                        </div>
                      </div>
                      {Array.isArray(line.attachment_files) && line.attachment_files.length > 0 ? (
                        <div className="mt-1 space-y-1">
                          {line.attachment_files.slice(0, 2).map((attachment) => (
                            <div key={attachment.id} className="truncate text-[11px] text-muted-foreground">
                              {attachment.filename || 'Mill cert file'}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </td> : null}
                    <td className="px-3 py-2">
                      {isReturnGrn ? (
                        <Select
                          value={draft.putawayLocationCode || (line.putaway_location_id ? binCodeById.get(String(line.putaway_location_id)) : '') || undefined}
                          onValueChange={(value) => updateDraft(line.id, { putawayLocationCode: value })}
                          disabled={lineLocked}
                        >
                          <SelectTrigger className="h-8 min-w-[180px]">
                            <SelectValue placeholder="Select location" />
                          </SelectTrigger>
                          <SelectContent>
                            {bins.map((bin) => (
                              <SelectItem key={bin.id} value={bin.code}>
                                {bin.zone_name ? `${bin.code} · ${bin.zone_name}` : bin.code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          className="h-8 min-w-[140px]"
                          value={draft.putawayLocationCode}
                          disabled={lineLocked && !traceEditable}
                          onChange={(event) => updateDraft(line.id, { putawayLocationCode: event.target.value })}
                          placeholder="Putaway loc"
                        />
                      )}
                    </td>
                    {!isReturnGrn ? <td className="px-3 py-2">{line.uom ?? '-'}</td> : null}
                    {!isReturnGrn ? <td className="px-3 py-2">{line.inspection_required ? 'Yes' : 'No'}</td> : null}
                    {!isReturnGrn ? <td className="px-3 py-2">{qcReadyQty(line)}</td> : null}
                    <td className="sticky right-0 z-10 border-l bg-background px-3 py-2">
                      <div className="flex min-w-[168px] items-center gap-2">
                        {!lineLocked ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void receiveLine(line)}
                            disabled={savingLineId === line.id || confirming || deletingLineId === line.id}
                          >
                            {savingLineId === line.id || confirming ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                            {isReturnGrn ? 'Received' : 'Receive'}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">Received</span>
                        )}
                        {canEditReceiving && linePendingReceiving ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => void deleteLine(line)}
                            disabled={Boolean(savingLineId) || confirming || deletingLineId === line.id}
                          >
                            {deletingLineId === line.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {detailLines.length === 0 ? (
                <tr>
                  <td colSpan={17} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No inbound lines found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <Dialog open={poSelectorOpen} onOpenChange={setPoSelectorOpen}>
        <DialogContent dock="right" className="flex h-[min(88vh,900px)] w-[min(100vw-32px,980px)] max-w-none flex-col overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>Add PO Lines to GRN</DialogTitle>
            <DialogDescription>
              Select open PO lines to add into {detail.asn_number ?? 'this GRN'}. Existing GRN draft lines are counted, so add qty cannot exceed the remaining PO qty.
            </DialogDescription>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-6 py-4">
            <div className="grid gap-3 rounded-md border bg-slate-50 p-3 text-sm sm:grid-cols-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">PO No</div>
                <div className="font-medium">{poDetail?.order_number ?? detail.source_order_number ?? '-'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Supplier</div>
                <div className="font-medium">{poDetail?.counterparty_name ?? detail.origin_name ?? '-'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Project</div>
                <div className="font-medium">{poDetail?.project_code ?? detail.project_code ?? '-'}</div>
              </div>
            </div>

            {poSelectorLoading ? (
              <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading PO lines...
              </div>
            ) : (
              <div className="runtime-grid-scrollbar boox-x-scroll min-h-0 flex-1 overflow-auto rounded-md border">
                <table className="w-max min-w-[1040px] border-collapse text-sm">
                  <thead className="sticky top-0 z-10 bg-slate-50">
                    <tr className="border-b">
                      <th className="w-12 px-3 py-2 text-left">
                        <Checkbox
                          checked={poSelectableLines.length > 0 && selectedPoLineIds.length === poSelectableLines.length}
                          onCheckedChange={(checked) => {
                            setSelectedPoLineIds(checked === true ? poSelectableLines.map((line) => String(line.id)) : []);
                          }}
                          aria-label="Select all PO lines"
                        />
                      </th>
                      <th className="px-3 py-2 text-left font-medium">PO Line</th>
                      <th className="px-3 py-2 text-left font-medium">SKU Code</th>
                      <th className="px-3 py-2 text-left font-medium">Description</th>
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-right font-medium">Ordered</th>
                      <th className="px-3 py-2 text-right font-medium">In GRN</th>
                      <th className="px-3 py-2 text-right font-medium">Remaining</th>
                      <th className="px-3 py-2 text-left font-medium">Add Qty</th>
                      <th className="px-3 py-2 text-left font-medium">UOM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poSelectableLines.map((line) => {
                      const lineId = String(line.id);
                      const selected = selectedPoLineIds.includes(lineId);
                      return (
                        <tr key={lineId} className="border-b align-top">
                          <td className="px-3 py-2">
                            <Checkbox
                              checked={selected}
                              onCheckedChange={(checked) => {
                                setSelectedPoLineIds((current) => (
                                  checked === true
                                    ? Array.from(new Set([...current, lineId]))
                                    : current.filter((id) => id !== lineId)
                                ));
                              }}
                              aria-label={`Select PO line ${line.line_number ?? ''}`}
                            />
                          </td>
                          <td className="px-3 py-2">{line.line_number ?? '-'}</td>
                          <td className="px-3 py-2 font-medium">{line.sku_code ?? '-'}</td>
                          <td className="min-w-[260px] px-3 py-2">{line.sku_name ?? '-'}</td>
                          <td className="px-3 py-2">{line.item_type ?? '-'}</td>
                          <td className="px-3 py-2 text-right">{line.orderedQty}</td>
                          <td className="px-3 py-2 text-right">{line.draftedQty}</td>
                          <td className="px-3 py-2 text-right">{line.remainingQty}</td>
                          <td className="px-3 py-2">
                            <Input
                              className="h-8 w-24"
                              type="number"
                              min="1"
                              max={line.remainingQty}
                              step="1"
                              value={poLineQtyDrafts[lineId] ?? ''}
                              disabled={!selected}
                              onChange={(event) => setPoLineQtyDrafts((current) => ({
                                ...current,
                                [lineId]: event.target.value,
                              }))}
                            />
                          </td>
                          <td className="px-3 py-2">{line.uom ?? '-'}</td>
                        </tr>
                      );
                    })}
                    {poSelectableLines.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-8 text-center text-sm text-muted-foreground">
                          No remaining PO lines available to add.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center justify-end gap-2 border-t px-6 py-4">
            <Button variant="outline" onClick={() => setPoSelectorOpen(false)} disabled={addingPoLines}>
              Cancel
            </Button>
            <Button onClick={() => void addSelectedPoLines()} disabled={addingPoLines || selectedPoLineIds.length === 0 || poSelectorLoading}>
              {addingPoLines ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1.5 h-3.5 w-3.5" />}
              Add to GRN
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showStickerPanel} onOpenChange={setShowStickerPanel}>
        <DialogContent dock="right" className="flex h-[min(88vh,900px)] w-[min(100vw-32px,920px)] max-w-none flex-col overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>Product Stickers</DialogTitle>
            <DialogDescription>
              Configure receiving stickers for {detail.asn_number ?? 'this GRN'} before opening the print preview.
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            <WmsReceivingStickerPanel
              inboundOrderId={detail.id}
              onRequestPrintPreview={({ html, title }) => {
                setShowStickerPanel(false);
                setStickerPreviewTitle(title);
                setStickerPreviewHtml(html);
                setShowStickerPreview(true);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showStickerPreview} onOpenChange={setShowStickerPreview}>
        <DialogContent dock="right" className="flex h-[min(88vh,860px)] w-[min(100vw-32px,430px)] max-w-none flex-col overflow-hidden p-0">
          <div className="flex shrink-0 items-start justify-between gap-4 border-b px-6 py-4">
            <div>
              <DialogTitle>{stickerPreviewTitle}</DialogTitle>
              <DialogDescription>
                Embedded print preview. Use the button below to print or save PDF without opening a global popup window.
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => {
                  const frameWindow = stickerPreviewFrameRef.current?.contentWindow;
                  if (!frameWindow) {
                    toast.error('Print preview is not ready');
                    return;
                  }
                  frameWindow.focus();
                  frameWindow.print();
                }}
                disabled={!stickerPreviewHtml}
              >
                <Printer className="mr-1.5 h-3.5 w-3.5" />
                Print / Save PDF
              </Button>
            </div>
          </div>
          <div className="min-h-0 flex-1 bg-muted/20 p-4">
            {stickerPreviewHtml ? (
              <iframe
                ref={stickerPreviewFrameRef}
                title={stickerPreviewTitle}
                srcDoc={stickerPreviewHtml}
                className="h-full w-full rounded-md border bg-white"
              />
            ) : (
              <div className="flex h-full items-center justify-center rounded-md border border-dashed bg-background text-sm text-muted-foreground">
                Print preview is empty.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
